import React, { useEffect } from 'react';
import styled from 'styled-components';

import { SquareLoader } from '@hud/web-components/Loader/SquareLoader';
import { smRegular, xsRegular, xxlBold } from '@hud/web-components/theme/typography';
import { themeColor } from '@hud/web-components/theme/colors';
import { StepStyles as Styled } from './SetupSteps.styles';
import useAnalytics from '../../../hooks/useAnalytics';
import { HudIcon } from '@hud/web-components/Icons/HudIcons';
import SpinnerWithCheck, {
  SpinnerMode,
} from '@hud/web-components/SpinnerWithCheck/SpinnerWithCheck';
import { statusItems } from './InstallationSetupStepContent';
import InstallationSetupStepTip, { GlobalStyle } from './InstallationSetupStepTip';

const SHOW_TIP_DELAY = 60; // seconds

interface InstallationInProgressStepProps {
  installationSetupStep: number;
  secondsSinceStepStart: number;
}

export function InstallationInProgressStep({
  installationSetupStep,
  secondsSinceStepStart,
}: InstallationInProgressStepProps) {
  const analytics = useAnalytics();

  useEffect(() => {
    analytics.capture('sdk_setup_screen_session_initiated');
  }, [analytics]);

  const showingTimeoutTip =
    statusItems[installationSetupStep + 1]?.timeoutTip &&
    secondsSinceStepStart > SHOW_TIP_DELAY;

  useEffect(() => {
    if (analytics && showingTimeoutTip) {
      analytics.capture('sdk_setup_screen_warning_displayed', {
        currentStep: statusItems[installationSetupStep + 1].statusKey,
      });
    }
  }, [showingTimeoutTip]);

  return (
    <StyledContainer>
      <GlobalStyle />
      <StyledBody>
        <SquareLoader size={40} />
        <div>
          <Title>Installation in progress...</Title>
          <Subtitle>This may take a minute or two</Subtitle>
        </div>
        <ul>
          {statusItems.map((item, index) => {
            let mode: SpinnerMode = SpinnerMode.NOT_STARTED;
            if (installationSetupStep === index - 1) {
              mode = SpinnerMode.LOADING;
            } else if (installationSetupStep >= index) {
              mode = SpinnerMode.SUCCESS;
            }

            const isTimeoutTip =
              'timeoutTip' in item &&
              installationSetupStep === index - 1 &&
              secondsSinceStepStart > SHOW_TIP_DELAY;
            let tipContent = null;
            if (isTimeoutTip) {
              tipContent = item.timeoutTip;
            } else if ('tip' in item && installationSetupStep === index - 1) {
              tipContent = item.tip;
            }
            const tipElement = (
              <InstallationSetupStepTip
                open={tipContent !== null}
                emphasis={isTimeoutTip}
              >
                {tipContent}
              </InstallationSetupStepTip>
            );

            return (
              <li key={item.statusKey} className={mode}>
                <div className="title">
                  <SpinnerWithCheck mode={mode} />
                  {item.label}
                </div>
                {tipElement}
              </li>
            );
          })}
        </ul>
        <BottomText>
          <HudIcon icon="webviewMenu.info" size="18px" />
          <span>Tip: Detailed logs are also available in your terminal.</span>
        </BottomText>
      </StyledBody>
    </StyledContainer>
  );
}

const StyledContainer = styled(Styled.Container)`
  justify-content: center;
`;

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 60px;
  overflow-y: auto;
  text-align: center;
  height: 100%;

  ul {
    display: flex;
    flex-direction: column;
    gap: 24px;
    list-style-type: none;
    padding: 0;
    margin: 36px 40px;
  }

  li {
    ${smRegular};
    text-align: left;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: color 1s ease-in;

    &.success .title {
      color: ${themeColor('turquoise')};
    }

    svg {
      position: relative;
      top: -1px;
    }
  }

  li .title {
    display: flex;
    flex-direction: row;
    gap: 8px;
  }

  BottomText {
    margin-top: 24px;
  }
`;

const Title = styled.div`
  ${xxlBold};
  color: ${themeColor('textPrimary')};
  margin-bottom: 4px;
`;

const Subtitle = styled.div`
  ${smRegular};
  color: ${themeColor('textSecondary')};
`;

const BottomText = styled.div`
  ${xsRegular};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${themeColor('textTertiary')};
  gap: 8px;
  margin-top: auto;
`;
